import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { ESTADO_PETICION, ESTADO_COMPONENTE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ScanSearch, Loader2, X, MapPin, Tag, Building2, ChevronRight } from "lucide-react";

/**
 * Buscador profundo de líneas/componentes: ciudad, marca, espacio, ref. cliente, componente…
 *
 * - Sin `onSelect`: cada resultado es un Link a la petición (uso en Solicitudes de trabajo).
 * - Con `onSelect(hit, componente)`: el resultado (o un chip de componente) invoca el callback
 *   para que la pantalla anfitriona localice/enfoque el elemento (Materiales de montaje,
 *   Diseño y producción).
 */
export default function BuscadorLineas({ onResults, onSelect, placeholder, hint, testId = "buscador-lineas" }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setData(null); onResults?.(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/buscador/lineas", { params: { q: term } });
        setData(d);
        setOpen(true);
        onResults?.(d.peticion_ids || []);
      } catch { setData(null); onResults?.(null); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const limpiar = () => { setQ(""); setData(null); onResults?.(null); };
  const seleccionar = (hit, comp) => {
    onSelect?.(hit, comp || hit.componentes?.[0] || null);
    setOpen(false);
  };

  return (
    <div className="mb-5 rounded-xl border border-cyan-500/25 bg-cyan-500/[0.04]" data-testid={testId}>
      <div className="relative">
        <ScanSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-cyan-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          data-testid={`${testId}-input`}
          placeholder={placeholder || "Buscar líneas y componentes: ciudad, marca, espacio, ref. cliente, componente…"}
          className="pl-9 pr-9 bg-transparent border-0 focus-visible:ring-0 text-zinc-100 placeholder:text-zinc-500" />
        {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400 animate-spin" />}
        {!loading && q && (
          <button onClick={limpiar} data-testid={`${testId}-clear`}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"><X className="w-4 h-4" /></button>
        )}
      </div>
      {data && (
        <div className="border-t border-cyan-500/20">
          <div className="flex items-center justify-between px-3 py-1.5 text-[11px] text-zinc-400">
            <span data-testid={`${testId}-total`}>
              <span className="text-cyan-300 font-semibold">{data.total}</span> línea(s) en{" "}
              <span className="text-cyan-300 font-semibold">{data.peticion_ids?.length || 0}</span> petición(es)
              {" · "}{hint || (onSelect ? "pulsa un resultado para localizarlo" : "la lista de abajo queda filtrada")}
            </span>
            <button onClick={() => setOpen((v) => !v)} className="underline hover:text-zinc-200">{open ? "Ocultar detalle" : "Ver detalle"}</button>
          </div>
          {open && (
            <div className="max-h-72 overflow-auto divide-y divide-zinc-800/60" data-testid={`${testId}-results`}>
              {data.items.length === 0 && <div className="px-3 py-4 text-sm text-zinc-500 text-center">Sin coincidencias</div>}
              {data.items.map((it) => (
                <ResultadoLinea key={it.linea_id} it={it} onSelect={onSelect ? seleccionar : null} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultadoLinea({ it, onSelect }) {
  const est = ESTADO_PETICION[it.peticion_estado];
  const cls = "flex items-start gap-3 px-3 py-2 hover:bg-zinc-900/60 transition-colors group w-full text-left";
  const inner = (
    <>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="font-mono text-amber-300">{it.peticion_codigo}</span>
          <span className="text-zinc-300 truncate max-w-[280px]">{it.peticion_nombre}</span>
          {est && <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${est.color || "bg-zinc-800 text-zinc-300"}`}>{est.label}</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-1 text-[10px]">
          {it.cliente_nombre && <span className="inline-flex items-center gap-1 text-zinc-400"><Building2 className="w-3 h-3" />{it.cliente_nombre}</span>}
          {it.espacio_nombre && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/30">
              <MapPin className="w-2.5 h-2.5" />{it.espacio_nombre}{it.ciudad ? ` · ${it.ciudad}` : ""}
            </span>
          )}
          {it.marca && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300 border border-violet-500/30"><Tag className="w-2.5 h-2.5" />{it.marca}</span>}
          {it.referencia_cliente && <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-200 border border-amber-500/30 font-mono">Ref. {it.referencia_cliente}</span>}
          {it.tipo && <span className="text-zinc-500">{it.tipo}</span>}
        </div>
        {it.componentes?.length > 0 && (
          <div className="mt-1 text-[11px] text-zinc-400 flex items-center gap-1 flex-wrap">
            <span>{it.match_en_componente ? "🎯" : "🧩"}</span>
            {it.componentes.map((c) => {
              const ec = ESTADO_COMPONENTE[c.estado];
              const label = c.nombre + (c.referencia_cliente ? ` (Ref. ${c.referencia_cliente})` : "");
              return onSelect ? (
                <button key={c.id} type="button"
                  onClick={(e) => { e.stopPropagation(); onSelect(it, c); }}
                  data-testid={`buscador-comp-${c.id}`}
                  title={ec ? `Estado: ${ec.label}` : undefined}
                  className="px-1.5 py-0.5 rounded bg-zinc-800/80 border border-zinc-700 hover:border-cyan-400/60 hover:text-cyan-200 transition-colors">
                  {label}{ec && <span className={`ml-1 text-[9px] px-1 rounded ${ec.color}`}>{ec.label}</span>}
                </button>
              ) : (
                <span key={c.id} className="truncate">{label}</span>
              );
            })}
          </div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-300 shrink-0 mt-1" />
    </>
  );
  if (onSelect) {
    return (
      <div role="button" tabIndex={0} onClick={() => onSelect(it, null)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(it, null); } }}
        data-testid={`buscador-hit-${it.linea_id}`} className={`${cls} cursor-pointer focus:outline-none focus:bg-zinc-900/60`}>
        {inner}
      </div>
    );
  }
  return (
    <Link to={`/peticiones/${it.peticion_id}#linea-${it.linea_id}`} data-testid={`buscador-hit-${it.linea_id}`} className={cls}>
      {inner}
    </Link>
  );
}
